'use server'

import { createHash } from 'node:crypto'
import { createClient } from '@/lib/supabase/server'
import { createAdminClient } from '@/lib/supabase/admin'
import { audioUrl } from '@/lib/audio-url'
import { hasTurkish, normalizeTr, ttsDottedI } from '@/lib/tr-text'
import { deviceId, getAiUsage, guestCacheIpAllowed, recordAiUsage, usageExhausted } from '@/lib/ai-usage'
import { headers } from 'next/headers'
import type { SupabaseClient } from '@supabase/supabase-js'

// Озвучка турецкого текста по кнопке 🔊 — гибрид (решение пользователя
// 2026-09-10):
//   1. готовая озвучка уроков (vocabulary.audio_url / example_audio_url —
//      тот же голос, заранее сгенерированный generate-tts.mjs);
//   2. общий кэш tts_cache (mp3 в бакете tts-audio/live/);
//   3. Google Cloud TTS на лету, результат — в кэш.
// Если ничего не вышло — { url: null }, и клиент (lib/speak.ts) говорит
// голосом браузера: кнопка звука не молчит, даже если Google станет
// недоступен с боевого сервера.

const TTS_ENDPOINT = 'https://texttospeech.googleapis.com/v1/text:synthesize'
const BUCKET = 'tts-audio'
// Тот же голос, что у слов в уроках (generate-tts.mjs, VOCAB_VOICE).
const VOICE = 'tr-TR-Chirp3-HD-Charon'
const MAX_TEXT = 500
const TTS_TIMEOUT_MS = 10_000

// 'limit' — недельный лимит синтеза бесплатного тарифа исчерпан (lib/ai-usage.ts);
// для клиента это как 'none': говорит голос браузера.
export type TtsResult = { url: string | null; source: 'lesson' | 'cache' | 'generated' | 'none' | 'limit' }

// Ключ кэша: регистр и знаки препинания сохраняем — от «?» и «!» зависит
// интонация; схлопываются только пробелы.
function ttsKey(text: string) {
  return createHash('sha1').update(`${VOICE}|${text}`).digest('hex')
}

function escapeLike(s: string) {
  return s.replace(/[%_\\]/g, (c) => `\\${c}`)
}

async function findLessonAudio(supabase: SupabaseClient, text: string): Promise<string | null> {
  const normalized = normalizeTr(text)
  if (!normalized) return null
  // Как в translate/actions.ts: ilike только сужает выборку (PostgreSQL не
  // знает турецкого I/ı), точное сравнение — в JS по турецкой нормализации.
  const pattern = escapeLike(normalized)
  const [{ data: words }, { data: examples }] = await Promise.all([
    supabase.from('vocabulary').select('turkish, audio_url, grammar_tag').ilike('turkish', pattern).not('audio_url', 'is', null).limit(5),
    supabase.from('vocabulary').select('example_tr, example_audio_url').ilike('example_tr', `${pattern}%`).not('example_audio_url', 'is', null).limit(5),
  ])
  // Буквы алфавита («A a») озвучены как буква, а не как слово — не подходят.
  const word = (words ?? []).find((w) => w.grammar_tag !== 'alphabet' && normalizeTr(w.turkish) === normalized)
  if (word?.audio_url) return word.audio_url
  const example = (examples ?? []).find((e) => e.example_tr && normalizeTr(e.example_tr) === normalized)
  return example?.example_audio_url ?? null
}

// Chirp3-HD на коротком слове без знаков препинания («et», «su») отдаёт
// обрывок ~0.26 с, который не слышно. С заглавной и с точкой — нормальное
// слово (см. ttsFriendly в scripts/generate-tts.mjs). Только текст для TTS.
function ttsFriendly(text: string): string {
  const capital = text.charAt(0).toLocaleUpperCase('tr-TR') + text.slice(1)
  return /[.!?…]$/.test(capital) ? capital : `${capital}.`
}

async function synthesize(text: string): Promise<Buffer> {
  const apiKey = process.env.GOOGLE_TTS_API_KEY
  if (!apiKey) throw new Error('GOOGLE_TTS_API_KEY не задан')
  const res = await fetch(`${TTS_ENDPOINT}?key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      input: { text: ttsDottedI(ttsFriendly(text)) },
      voice: { languageCode: 'tr-TR', name: VOICE },
      audioConfig: { audioEncoding: 'MP3' },
    }),
    signal: AbortSignal.timeout(TTS_TIMEOUT_MS),
  })
  if (!res.ok) throw new Error(`TTS API ${res.status}`)
  const data = (await res.json()) as { audioContent?: string }
  if (!data.audioContent) throw new Error('TTS API без audioContent')
  return Buffer.from(data.audioContent, 'base64')
}

// Гость в уроке без входа (lib/guest-lesson.ts, план .scratch/guest-mode, шаг 5):
// только записанная озвучка и общий кэш, без синтеза; потолок на IP. Кэш
// читают только вошедшие, поэтому клиент service-role. Нет записи - голос браузера.
async function guestAudio(text: string): Promise<TtsResult> {
  try {
    const ip = (await headers()).get('x-forwarded-for')?.split(',')[0].trim() ?? null
    if (!guestCacheIpAllowed(ip)) return { url: null, source: 'none' }
    const admin = createAdminClient()
    const lessonPath = await findLessonAudio(admin, text)
    if (lessonPath) return { url: audioUrl(lessonPath), source: 'lesson' }
    const { data: cached } = await admin.from('tts_cache').select('audio_path').eq('key', ttsKey(text)).maybeSingle()
    return cached?.audio_path ? { url: audioUrl(cached.audio_path), source: 'cache' } : { url: null, source: 'none' }
  } catch (e) {
    console.error('[tts guest]', e instanceof Error ? e.message : e)
    return { url: null, source: 'none' }
  }
}

// cacheOnly - только готовая озвучка и кэш, без синтеза: так звучат игры
// (их фразы озвучены заранее, scripts/generate-game-tts.mjs), лимит не тратится.
export async function getTurkishAudio(rawText: string, { cacheOnly = false }: { cacheOnly?: boolean } = {}): Promise<TtsResult> {
  const text = rawText.replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT)
  if (!text || !hasTurkish(text)) return { url: null, source: 'none' }

  const supabase = await createClient()
  const { data: claims, error: claimsError } = await supabase.auth.getClaims()
  if (claimsError || !claims?.claims) return guestAudio(text)

  try {
    // 1. Озвучка из уроков.
    const lessonPath = await findLessonAudio(supabase, text)
    if (lessonPath) return { url: audioUrl(lessonPath), source: 'lesson' }

    // 2. Кэш.
    const key = ttsKey(text)
    const { data: cached } = await supabase.from('tts_cache').select('audio_path').eq('key', key).maybeSingle()
    if (cached?.audio_path) return { url: audioUrl(cached.audio_path), source: 'cache' }
    if (cacheOnly) return { url: null, source: 'none' }

    // 3. Синтез в Google (США) — это трансграничная передача: в тексте могут
    // быть реплики ученика или ответы AI на них. TK_TTS_LIVE=off её
    // выключает — на 10 рабочих дней после уведомления РКН (раньше передавать
    // нельзя) и насовсем, если РКН запретит. Озвучка уроков и кэш выше
    // продолжают работать: они лежат у нас; новое звучит голосом браузера.
    if (process.env.TK_TTS_LIVE === 'off') return { url: null, source: 'none' }

    // Только в пределах недельного лимита бесплатного тарифа.
    const userId = claims.claims.sub
    const device = await deviceId(true)
    const usage = await getAiUsage(supabase, userId, 'tts', device)
    if (usageExhausted(usage)) return { url: null, source: 'limit' }

    const buffer = await synthesize(text)
    await recordAiUsage(userId, 'tts', device)
    const admin = createAdminClient()
    const audioPath = `live/${key}.mp3`
    const { error: uploadError } = await admin.storage.from(BUCKET).upload(audioPath, buffer, { contentType: 'audio/mpeg', upsert: true })
    if (uploadError) throw new Error(uploadError.message)
    await admin.from('tts_cache').upsert({ key, text, voice: VOICE, audio_path: audioPath }, { onConflict: 'key', ignoreDuplicates: true })
    return { url: audioUrl(audioPath), source: 'generated' }
  } catch (e) {
    console.error('[tts]', e instanceof Error ? e.message : e)
    return { url: null, source: 'none' }
  }
}
